import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, toast } from '@qodo/design-system';
import { OIDC_PROVIDER } from 'network/httpClients/OIDCAuthenticationClient/consts';
import { QUERY_PARAMS, ROUTES } from 'routes/consts';
import { mixpanelService } from 'services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from 'services/mixpanel/consts';
import { buildMixpanelErrorData } from 'services/mixpanel/utils';
import { Alert, AlertDescription } from 'ui/alert';
import { useLocalStorage } from 'usehooks-ts';
import { spawn } from 'utils/translations';
import { getQueryParam } from 'utils/url';
import {
  INITIAL_SEND_AGAIN_CLICK_VALUE,
  SEND_AGAIN_FREEZE_DURATION,
  SEND_AGAIN_MAX_ATTEMPTS,
} from '../consts';
import { useEmailSender } from '../useEmailSender';
import { createSendAgainLocalStorageKey } from '../utils';
import { Title } from './Title';

export const CheckYourEmail = () => {
  const [isLoading, setIsLoading] = useState(false);
  const email = getQueryParam(QUERY_PARAMS.EMAIL) || undefined;

  const spawnToast = spawn('toasts.check-your-email.send-again');

  const { sendEmail } = useEmailSender();

  const localStorageKey = createSendAgainLocalStorageKey(email);
  const [sendAgainClickData, setSendAgainClickData] = useLocalStorage(
    localStorageKey,
    INITIAL_SEND_AGAIN_CLICK_VALUE,
  );

  const spawnText = spawn('login.check-your-email');
  const navigate = useNavigate();

  const isSendAgainDisabled =
    sendAgainClickData.count >= SEND_AGAIN_MAX_ATTEMPTS;

  const resetSendAgainClickData = () => {
    setSendAgainClickData(INITIAL_SEND_AGAIN_CLICK_VALUE);
  };

  const onSendAgainClick = async () => {
    if (isSendAgainDisabled || !email) {
      return;
    }

    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.REQUEST_EMAIL_RESEND,
    });

    const hasTimeElapsed =
      Date.now() - sendAgainClickData.timestamp >= SEND_AGAIN_FREEZE_DURATION;
    const shouldResetSendAgainClickData =
      sendAgainClickData.count > 0 && hasTimeElapsed;
    const isFirstClick = sendAgainClickData.count === 0;

    // Reset the send again data, and then continue with the flow for sending the link
    if (shouldResetSendAgainClickData) {
      resetSendAgainClickData();
    }

    try {
      setIsLoading(true);
      setSendAgainClickData({
        count: sendAgainClickData.count + 1,
        timestamp: isFirstClick ? Date.now() : sendAgainClickData.timestamp,
      });

      await sendEmail(email!);
      toast.success(spawnToast('success'));
    } catch (error) {
      toast.error(spawnToast('error'));

      mixpanelService.track(MIXPANEL_EVENT_NAMES.ERROR, {
        operation: MIXPANEL_OPERATIONS.LOGIN_INTERRUPTED,
        error_data: buildMixpanelErrorData(error, OIDC_PROVIDER.EMAIL),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onDifferentSignupMethodClick = () => {
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.CHOOSE_DIFFERENT_SIGNUP_METHOD,
    });

    navigate(ROUTES.SIGNIN);
  };

  if (!email) {
    return <Navigate to={ROUTES.SIGNIN} />;
  }

  return (
    <section className="flex flex-col items-center gap-7 max-w-100">
      <Title>{spawnText('title')}</Title>
      <Alert className=" w-full bg-fill-panel-neutral p-2 dark:border-0 px-3 rounded-md">
        <AlertDescription>{email}</AlertDescription>
      </Alert>

      <div className="flex w-full flex-wrap gap-4">
        <Button
          variant="outline"
          onClick={onSendAgainClick}
          loading={isLoading}
          disabled={isSendAgainDisabled}
          tooltip={
            isSendAgainDisabled ? spawnText('send-again.tooltip') : undefined
          }
        >
          {spawnText('send-again')}
        </Button>
        <Button
          variant="link"
          onClick={onDifferentSignupMethodClick}
          className="p-0"
        >
          {spawnText('different-signup-method-button')}
        </Button>
      </div>
    </section>
  );
};
